import { useEffect, useState } from 'react';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import {
  EmptyIndicator,
  Owners,
  Pagination,
  TitleWithIcon,
  UserPathStepProgress
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { PathCompletionStatus } from 'components/paths';
import { UsersCompletionFilter } from 'components/users';

import type { GetUserPathsRes } from 'shared-types/responses';
import type { PageState } from 'types';

interface State {
  loading: boolean;
  pages: number;
  page: number;
  paths: GetUserPathsRes['paths'];
}

const ProfilePaths = ({ isUserPage }: { isUserPage?: boolean }) => {
  const { dispatch } = useDispatch();
  const {
    messages,
    pageState: { pathCompletionStatus = 'all', user },
    serverConfig
  } = useReduxState();

  const [state, setState] = useState<State>({
    loading: true,
    page: 1,
    pages: 0,
    paths: null
  });

  const updatePaths = async ({
    completion,
    page
  }: {
    completion?: PageState['completionStatus'];
    page?: number;
  }) => {
    try {
      setState({ ...state, loading: true });
      const completionValue = completion || pathCompletionStatus;
      const pageValue = page || state.page;
      const paths = await api.get(
        `/admin/users/${user.id}/paths` +
          `?page=${pageValue}` +
          `&limit=10` +
          `&completionStatus=${completionValue}`
      );

      setState({
        ...state,
        loading: false,
        page: pageValue,
        pages: paths.data.pages,
        paths: paths.data.paths
      });
    } catch (err) {
      setState({ ...state, loading: false });
      await dispatch.handleErrorDispatch(err);
    }
  };

  const updateCompletionStatus = async ({ pathCompletionStatus }) => {
    await updatePaths({
      completion: pathCompletionStatus,
      page: 1
    });
  };

  useEffect(() => {
    updatePaths({});
  }, []);

  const { loading, page, pages, paths } = state;

  return (
    <div className="paths">
      <Box
        headerContent={
          <UsersCompletionFilter
            disabled={false}
            updateUsers={updateCompletionStatus}
            view="path"
          />
        }
        loading={loading}
        padding={0}
        title={messages.paths}
      >
        {paths && paths.length ? (
          <BoxTable>
            <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
              <BoxTableCell>{messages.commonTitle}</BoxTableCell>
              <BoxTableCell>{messages.commonProgress}</BoxTableCell>
              <BoxTableCell>{messages.commonCompleted}</BoxTableCell>
            </BoxTableHeader>

            {paths.map((path) => (
              <BoxTableRow key={path.id}>
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  <TitleWithIcon
                    icon="tasks"
                    style={{ alignItems: 'center' }}
                    title={path.title}
                    titleLink={
                      isUserPage
                        ? {
                            as: `/users/${user.id}/paths/${path.id}`,
                            href: '/users/[userId]/paths/[pathId]'
                          }
                        : {
                            as: `/profile/paths/${path.id}`,
                            href: '/profile/paths/[pathId]'
                          }
                    }
                  >
                    {!serverConfig.productHideOwner && (
                      <Owners owners={path.owners} />
                    )}
                  </TitleWithIcon>
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.commonProgress}
                >
                  <UserPathStepProgress progressData={path.progressData} />
                </BoxTableCell>
                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.commonCompleted}
                >
                  {path.progressData?.pathCompletion ? (
                    <PathCompletionStatus
                      pathCompletion={path.progressData.pathCompletion}
                      showDate={true}
                    />
                  ) : null}
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        ) : (
          <EmptyIndicator icon="tasks" title={messages.pathsEmpty} />
        )}
      </Box>

      <Pagination
        currentPage={page}
        numberOfPages={pages}
        onPageChange={updatePaths}
      />
    </div>
  );
};

export default ProfilePaths;
